/* ============================================================================
   Blair TLV cart-drawer helpers (Claude 2026-07-10)
   Two cross-cutting fixes that must hold on EVERY open/update path:

   1. Header-flush open — when the cart opens while the user has scrolled down
      (sticky header hidden), reveal + pin the header at the top and sync
      --header-bottom-position, so the drawer sits flush with no gap on top.

   2. Cart-icon X swap — the header cart <a id="cart-icon-bubble"> carries a bag
      glyph (.header__cart-open-icon) and an X glyph (.header__cart-close-icon)
      that header CSS toggles on body.cart-open. But add-to-cart (renderContents)
      and quantity changes (CartItems.updateQuantity) both overwrite that <a>
      with sections/cart-icon-bubble.liquid, which lacks BOTH the open-icon class
      and the X span — so the swap silently stops working. We cache the X once
      and re-apply the structure after any re-render.
   ============================================================================ */
let blairCartCloseIconHTML = '';

function blairCaptureCartCloseIcon() {
  if (blairCartCloseIconHTML) return;
  const closeIcon = document.querySelector('#cart-icon-bubble .header__cart-close-icon');
  if (closeIcon) blairCartCloseIconHTML = closeIcon.outerHTML;
}

function blairRestoreCartIconStructure() {
  const link = document.getElementById('cart-icon-bubble');
  if (!link) return;
  // Ensure the bag glyph carries the class the header hides while the cart is open.
  let openIcon = link.querySelector('.header__cart-open-icon');
  if (!openIcon) {
    openIcon = link.querySelector('.svg-wrapper:not(.header__cart-close-icon)');
    if (openIcon) openIcon.classList.add('header__cart-open-icon');
  }
  // Re-insert the X glyph if a section re-render dropped it.
  if (!link.querySelector('.header__cart-close-icon') && blairCartCloseIconHTML) {
    if (openIcon) openIcon.insertAdjacentHTML('afterend', blairCartCloseIconHTML);
    else link.insertAdjacentHTML('afterbegin', blairCartCloseIconHTML);
  }
}

function blairRevealHeaderForCart() {
  const header = document.querySelector('.section-header');
  if (!header) return;
  // The drawer is positioned at var(--header-bottom-position). This theme's header
  // is position:relative (sticky header is off), so once the page is scrolled the
  // header is off-screen and that offset opens a gap showing page content behind
  // the drawer. Bring the page back to the top so the drawer sits flush under a
  // visible header. Instant, not smooth: a smooth scroll would show the gap for
  // the whole scroll duration while the drawer is already unrolling.
  header.classList.remove('shopify-section-header-hidden');
  if (header.getBoundingClientRect().top < 0) {
    window.scrollTo(0, 0);
  }
  // Sync the offset the drawer is positioned against.
  const height = Math.round(header.getBoundingClientRect().height);
  if (height > 0) {
    document.documentElement.style.setProperty('--header-bottom-position', `${height}px`);
  }
}

// Quantity changes inside the drawer re-render the cart icon and then publish
// cartUpdate — restore the icon structure once that has happened.
if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => blairRestoreCartIconStructure());
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    // Cache the header X glyph while the markup is still intact (page load).
    blairCaptureCartCloseIcon();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    // Blair 2026-07-09: the header cart icon toggles the drawer (it turns into an X
    // while open, so clicking it again closes), matching the nav menu behavior.
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      if (this.classList.contains('active')) this.close();
      else this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        if (this.classList.contains('active')) this.close();
        else this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    // Reveal + pin the header so the drawer opens flush beneath it (no top gap
    // when the page was scrolled), and guarantee the cart-open X icon is intact.
    blairRevealHeaderForCart();
    blairRestoreCartIconStructure();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.body.classList.add('cart-open');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('cart-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // The cart-icon-bubble section markup lacks the open-icon class + X glyph —
    // rebuild them so the bag→X swap keeps working after add-to-cart.
    blairRestoreCartIconStructure();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
